import { useQuery } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'

interface RecoveryInvoice {
  id: number
  number: number
  balance: string
  balance_cents: number
  path: string
}

interface AutopayRecovery {
  id: number
  person_id: number
  person_name: string
  failed_on: string
  decline_kind: 'soft' | 'hard' | 'needs_member_action'
  disable_reason: string | null
  invoice: RecoveryInvoice
}

interface AutopayRecoveriesResponse {
  autopay_recoveries: AutopayRecovery[]
}

const declineLabels: Record<AutopayRecovery['decline_kind'], string> = {
  soft: 'Soft decline',
  hard: 'Hard decline',
  needs_member_action: 'Member action required',
}

const columns: ListPageColumn<AutopayRecovery>[] = [
  {
    id: 'member',
    header: 'Member',
    cell: (item) => <span className="font-medium text-foreground">{item.person_name}</span>,
  },
  {
    id: 'failed-on',
    header: 'Latest failure',
    cell: (item) => displayDate(item.failed_on),
  },
  {
    id: 'decline',
    header: 'Decline',
    cell: (item) => <Badge variant="warning">{declineLabels[item.decline_kind]}</Badge>,
  },
  {
    id: 'reason',
    header: 'Disable reason',
    cell: (item) => item.disable_reason ?? '—',
  },
  {
    id: 'invoice',
    header: 'Invoice',
    cell: (item) => <span className="font-medium text-foreground">#{item.invoice.number}</span>,
  },
  {
    id: 'balance',
    header: 'Open Balance',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (item) => item.invoice.balance,
  },
]

export function FailedAutopayRecoveryPage({
  onOpenInvoice,
}: {
  onOpenInvoice: (invoiceId: number) => void
}) {
  const query = useQuery({
    queryKey: ['autopay-recoveries'],
    queryFn: () => getJSON<AutopayRecoveriesResponse>('/api/v1/autopay_recoveries'),
    retry: false,
  })

  return (
    <ListPage
      title="Failed Autopay"
      table={{
        rows: query.data?.autopay_recoveries ?? [],
        columns,
        getRowId: (item) => item.id,
        getRowLabel: (item) => `${item.person_name}, Invoice #${item.invoice.number}`,
        onRowOpen: (item) => onOpenInvoice(item.invoice.id),
        state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
        loadingTitle: 'Loading failed Autopay renewals',
        emptyTitle: 'No failed Autopay renewals',
        errorTitle: 'Failed Autopay unavailable',
        errorDescription: 'Refresh and try again.',
      }}
    />
  )
}
